import { DataTable, Spacer } from '@porsche-code/kado-ui';
import {
  PHeading,
  PLinkPure,
  PTag,
} from '@porsche-design-system/components-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ApplicationTags, DateOutput } from '@/components';
import { App, APP_FALLBACK } from '@/config/app';
import { NAMESPACES } from '@/config/i18n';
import { useFeatureArticlesQuery } from '@/graphql';
import { useApplicationConfigs } from '@/hooks';

export const Route = createFileRoute('/news/')({
  component: News,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Trans i18nKey="featureArticles" />,
    };
  },
});

const PAGE_SIZE = 5;

function News() {
  const { t } = useTranslation(NAMESPACES);

  const { data: queryData, isPending } = useFeatureArticlesQuery();
  const appConfigs = useApplicationConfigs();

  const filteredArticles = useMemo(
    () =>
      queryData?.featureArticles
        .filter(
          (feature) =>
            feature.isPublished &&
            dayjs(feature.publishedAt, 'YYYY-MM-DD').isBefore(dayjs()),
        )
        .map((feature) => ({
          ...feature,
          apps: feature.apps.length > 0 ? feature.apps : [APP_FALLBACK],
        })),
    [queryData],
  );

  const columns = useMemo(() => {
    const columnHelper =
      createColumnHelper<NonNullable<typeof filteredArticles>[number]>();

    return [
      columnHelper.accessor('title', {
        cell: (data) => (
          <PLinkPure icon="none" stretch>
            <Link
              to="/news/$featureArticleId"
              params={{ featureArticleId: data.row.original.id }}
            >
              {data.getValue()}
            </Link>
          </PLinkPure>
        ),
        header: () => t('featureArticle.title'),
      }),
      columnHelper.accessor('apps', {
        cell: (data) => <ApplicationTags apps={data.getValue()} />,
        header: () => t('apps'),
        meta: {
          filterType: 'select',
          filterOptions: [
            ...new Set(filteredArticles?.flatMap((article) => article.apps)),
          ].map((app) => ({
            label: appConfigs[app as App].name,
            value: app,
          })),
        },
        filterFn: (row, columnId, filterValue) =>
          (row.getValue(columnId) as string[]).includes(filterValue),
      }),
      columnHelper.accessor('publishedAt', {
        cell: (data) => {
          const publishDate = data.getValue();

          return publishDate ? <DateOutput date={publishDate} /> : 'n/a';
        },
        header: () => t('publishDate'),
        enableColumnFilter: false,
      }),
      columnHelper.accessor('id', {
        cell: (data) =>
          !localStorage.getItem(
            `kado.readFeatureArticle.${data.row.original.id}`,
          ) &&
          dayjs(data.row.original.publishedAt).isAfter(dayjs('2023-08-22')) ? (
            <PTag variant="info-frosted">{t('unread')}</PTag>
          ) : null,
        header: () => null,
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ];
  }, [appConfigs, filteredArticles, t]);

  return (
    <>
      <PHeading aria-level={2} tag="h2" size="lg">
        {t('featureArticles')}
      </PHeading>
      <Spacer mb="md" />
      <DataTable
        caption={t('featureArticles')}
        isLoading={isPending}
        data={filteredArticles ?? []}
        columns={columns}
        idAccessor="id"
        pagination={{
          position: 'bottom',
          pageSize: PAGE_SIZE,
        }}
        i18n={{
          filterLabel: (columnName) =>
            t('common:iconLabel.filter', { columnName }),
          optionAll: t('common:all'),
          buttonReset: t('common:action.reset'),
          buttonFilter: t('common:action.filter'),
          actionSearch: t('common:action.search'),
          noData: t('common:noData'),
          minLabel: t('common:min'),
          maxLabel: t('common:max'),
        }}
      />
    </>
  );
}
